// src/pages/InscripcionPublica.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import Spinner from "react-js-loader";

const API = "https://mocaub1h8d.execute-api.us-east-1.amazonaws.com/prod";

const fileToBase64 = (file) =>
  new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result.split(",")[1]);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });

const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const validatePhone = (v) => /^\+?[\d\s\-()]{7,20}$/.test(v);
const validateDocumento = (v) => /^[A-Za-z0-9][A-Za-z0-9 ._-]{2,19}$/.test(String(v || "").trim());

export default function InscripcionPublica() {
  const { eventoId } = useParams();
  const [evento, setEvento] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    dni: "",
    email: "",
    whatsapp: "",
    gender: "",
    birth_date: "",
  });
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.post(`${API}/getEventsSensoria`, {
          loginType: "0",
        });
        let lista = [];
        if (Array.isArray(res.data)) lista = res.data;
        else if (res.data?.body) lista = JSON.parse(res.data.body);
        const ev = lista.find((e) => String(e.idEvento) === String(eventoId));
        setEvento(ev || { nombre: "Evento", idEvento: eventoId });
      } catch {
        setEvento({ nombre: "Evento", idEvento: eventoId });
      }
    };
    if (eventoId) load();
  }, [eventoId]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setErrorMsg("El archivo no puede superar 5 MB.");
      return;
    }
    setFile(f);
    setFileName(f.name);
  };

  const validate = () => {
    const { first_name, last_name, dni, email, whatsapp, gender, birth_date } = form;
    if (!first_name || !last_name || !dni || !email || !whatsapp || !gender || !birth_date)
      return "Completá todos los campos obligatorios.";
    if (!validateEmail(email)) return "Email inválido.";
    if (!validatePhone(whatsapp)) return "WhatsApp inválido (incluí código de país, ej: +54911...)";
    if (!validateDocumento(dni)) return "DNI/documento inválido.";
    return null;
  };

  const handleSubmit = async () => {
    setErrorMsg("");
    const err = validate();
    if (err) return setErrorMsg(err);

    setLoading(true);
    try {
      let payment_proof_b64 = null;
      let payment_proof_name = null;
      if (file) {
        payment_proof_b64 = await fileToBase64(file);
        payment_proof_name = file.name;
      }

      await axios.post(`${API}/registerInscripcion`, {
        ...form,
        idEvento: eventoId,
        status: "PENDIENTE",
        payment_proof_b64,
        payment_proof_name,
      });

      setSubmitted(true);
    } catch (e) {
      const msg = e.response?.data?.message || "";
      if (msg.toLowerCase().includes("dni") || msg.toLowerCase().includes("duplicado")) {
        setErrorMsg("Ese DNI o email ya está registrado para este evento.");
      } else {
        setErrorMsg("Ocurrió un error. Intentá de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ fontSize: 56, textAlign: "center", marginBottom: 16 }}>✅</div>
          <h2 style={{ textAlign: "center", marginBottom: 12 }}>¡Inscripción recibida!</h2>
          <p style={{ textAlign: "center", color: "#555", lineHeight: 1.5 }}>
            Tu solicitud está <strong>pendiente de aprobación</strong>.
            <br />
            Te llegará tu entrada por email y WhatsApp una vez confirmada.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {loading && (
        <div style={styles.loaderOverlay}>
          <Spinner type="bubble-top" bgColor="#dfa66b" color="#dfa66b" size={90} />
        </div>
      )}

      <div style={styles.card}>
        <h2 style={{ textAlign: "center", marginBottom: 4 }}>Inscripción</h2>
        {evento && (
          <p style={{ textAlign: "center", color: "#888", marginBottom: 20, fontSize: 14 }}>
            {evento.nombre}
          </p>
        )}

        <label style={styles.label}>Nombre *</label>
        <input style={styles.input} value={form.first_name} onChange={set("first_name")} placeholder="Juan" />

        <label style={styles.label}>Apellido *</label>
        <input style={styles.input} value={form.last_name} onChange={set("last_name")} placeholder="Pérez" />

        <label style={styles.label}>DNI *</label>
        <input style={styles.input} value={form.dni} onChange={set("dni")} placeholder="DNI / Pasaporte" />

        <label style={styles.label}>Email *</label>
        <input style={styles.input} type="email" value={form.email} onChange={set("email")} placeholder="juan@email.com" />

        <label style={styles.label}>WhatsApp * (con código de país)</label>
        <input style={styles.input} type="tel" value={form.whatsapp} onChange={set("whatsapp")} placeholder="+5491112345678" />

        <label style={styles.label}>Sexo *</label>
        <select style={styles.input} value={form.gender} onChange={set("gender")}>
          <option value="">Seleccioná...</option>
          <option value="M">Masculino</option>
          <option value="F">Femenino</option>
          <option value="Otro">Otro</option>
        </select>

        <label style={styles.label}>Fecha de nacimiento *</label>
        <input style={styles.input} type="date" value={form.birth_date} onChange={set("birth_date")} />

        <label style={styles.label}>Comprobante de pago (opcional)</label>
        <label style={styles.fileBtn}>
          <input type="file" accept="image/*,application/pdf" onChange={handleFile} style={{ display: "none" }} />
          {fileName ? `📎 ${fileName}` : "Adjuntar archivo"}
        </label>

        {errorMsg && <p style={styles.error}>{errorMsg}</p>}

        <button style={styles.btn} onClick={handleSubmit}>
          Enviar inscripción
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    background: "#dfa66b",
    minHeight: "100vh",
    padding: "40px 16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  loaderOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  card: {
    background: "#fff",
    width: "100%",
    maxWidth: 420,
    padding: 28,
    borderRadius: 18,
    boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
  },
  label: {
    fontWeight: "600",
    fontSize: 13,
    marginTop: 14,
    marginBottom: 4,
    color: "#444",
    display: "block",
  },
  input: {
    padding: "11px 14px",
    borderRadius: 10,
    width: "100%",
    border: "1px solid #ccc",
    fontSize: 15,
    boxSizing: "border-box",
    background: "#fafafa",
  },
  fileBtn: {
    display: "block",
    padding: "11px 14px",
    borderRadius: 10,
    border: "1.5px dashed #ccc",
    fontSize: 14,
    color: "#555",
    cursor: "pointer",
    textAlign: "center",
    marginTop: 4,
    background: "#fafafa",
  },
  btn: {
    background: "#dfa66b",
    color: "#000",
    padding: 14,
    width: "100%",
    borderRadius: 10,
    border: "none",
    marginTop: 24,
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: 16,
  },
  error: {
    color: "red",
    marginTop: 12,
    textAlign: "center",
    fontSize: 14,
  },
};
